import { isBlob, isImage, isPdf, isWord } from '@application/utils';
import { Avatar, InfoBox, InputFileField, Label } from '@presentation/components';
import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import { Form, useFormikContext } from 'formik';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { FaFilePdf, FaFileWord } from 'react-icons/fa';
import {
  TbArrowLeft,
  TbChecks,
  TbChevronRight,
  TbPaperclip,
  TbSend,
  TbTrash,
} from 'react-icons/tb';
import { useMediaQuery } from 'react-responsive';
import { Tooltip } from 'react-tooltip';
import * as Styled from './styled';
import type { MtMessagePageFormValues, MtMessagesPageProps } from './types';

export * from './types';

export const MtMessagesPage: FC<MtMessagesPageProps> = (props) => {
  const {
    messageSections,
    conversations,
    currentConversation,
    onConversationClick,
    onDeleteMessage,
    onDeleteConversation,
    mobileMessagesShown,
    mobileOnBackClick,
    onImageClick,
    otherAccountIsDeleted,
    messageError = false,
  } = props;
  const { translate } = useTranslation();

  const { values, setFieldValue, submitForm, isValid, isSubmitting } =
    useFormikContext<MtMessagePageFormValues>();

  useEffect(() => {
    setTimeout(() => {
      const element = document.getElementById('mt-message-box');
      if (element) {
        element.scrollTo(0, element.scrollHeight);
      }
    }, 1);
  }, [messageSections, mobileMessagesShown]);

  const srcForFile = useCallback((file: File) => {
    if (!file) return '';
    if (typeof window === 'undefined') return '';
    if (!(file instanceof File)) return '';
    return URL.createObjectURL(file);
  }, []);

  const mobileBreakpoint = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.small}px)` });

  const shouldDisplayImage = (name: string) => {
    return isImage(name) || isBlob(name);
  };
  const file = values?.file as File;

  return (
    <Styled.Wrapper>
      <Styled.SideWrapper mobileMessagesShown={mobileMessagesShown}>
        <Styled.Title>{translate('mt.messages.title')}</Styled.Title>
        {conversations.map((conversation, index) => {
          return (
            <Styled.UserWrapper
              key={index}
              active={conversation.id === currentConversation.id}
              onClick={() => onConversationClick?.(index)}
            >
              <Styled.UserTextWrapper>
                {conversation.hasUnseenMessages && <Styled.NotificationDot />}
                <Avatar size={36} src={conversation.image ?? PLACEHOLDER_IMAGE_USER} />
                <Styled.UserName>{conversation.name}</Styled.UserName>
                <Styled.ConversationForwardButton>
                  <TbChevronRight />
                </Styled.ConversationForwardButton>
              </Styled.UserTextWrapper>
            </Styled.UserWrapper>
          );
        })}
      </Styled.SideWrapper>
      <Styled.ContentWrapper mobileMessagesShown={mobileMessagesShown}>
        <Styled.TopWrapper>
          <Styled.ConversationBackButton onClick={mobileOnBackClick}>
            <TbArrowLeft />
          </Styled.ConversationBackButton>
          <Avatar size={36} src={currentConversation.image ?? PLACEHOLDER_IMAGE_USER} />
          <Styled.TopUserName>{currentConversation.name}</Styled.TopUserName>
          {currentConversation.label && (
            <Styled.TopLabel>
              <Label {...currentConversation.label} />
            </Styled.TopLabel>
          )}
          <Styled.ConversationDeleteIcon
            onClick={() => onDeleteConversation?.(currentConversation.id)}
          >
            <TbTrash />
          </Styled.ConversationDeleteIcon>
        </Styled.TopWrapper>
        <Styled.MessageSectionsWrapper id="mt-message-box">
          {messageSections.map((messageSection, index) => {
            return (
              <Styled.MessageSection key={index}>
                <Styled.MessageSectionTitle isToday={(messageSection.isToday ?? false).toString()}>
                  {messageSection.title}
                </Styled.MessageSectionTitle>
                <Styled.MessagesWrapper>
                  {messageSection.messages.map((message, messageIndex) => {
                    const showTime =
                      messageSection.messages[messageIndex + 1]?.isOwn !== message.isOwn;
                    const showSeen = message.isOwn;

                    return (
                      <Styled.MessageWrapper key={message.id} ownMessage={message.isOwn}>
                        <Styled.MessageBox ownMessage={message.isOwn}>
                          {message.file && (
                            <Styled.FileContentWrapper>
                              {shouldDisplayImage(message.file as string) && (
                                <Styled.MessageImage
                                  src={message.file}
                                  alt=""
                                  onClick={() => onImageClick?.(message.file)}
                                />
                              )}
                              {isPdf(message.file as string) && (
                                <Styled.MessageFile
                                  iconLeft={<FaFilePdf color="white" />}
                                  color="light"
                                  onClick={() => window.open(message.file, '_blank')}
                                >
                                  {message.file?.split('/').pop()}
                                </Styled.MessageFile>
                              )}
                              {isWord(message.file as string) && (
                                <Styled.MessageFile
                                  iconLeft={<FaFileWord color="white" />}
                                  color="light"
                                  onClick={() => window.open(message.file, '_blank')}
                                >
                                  {message.file?.split('/').pop()}
                                </Styled.MessageFile>
                              )}

                              {message.isOwn && !message.text && (
                                <Styled.MessageTrash>
                                  <TbTrash
                                    className="trash"
                                    onClick={() => onDeleteMessage?.(message.id)}
                                  />
                                </Styled.MessageTrash>
                              )}
                            </Styled.FileContentWrapper>
                          )}
                          {message.text && (
                            <Styled.MessageContentWrapper ownMessage={message.isOwn}>
                              <Styled.MessageText ownMessage={message.isOwn}>
                                {message.text}
                              </Styled.MessageText>

                              {message.isOwn && (
                                <Styled.MessageTrash>
                                  <TbTrash
                                    className="trash"
                                    onClick={() => onDeleteMessage?.(message.id)}
                                  />
                                </Styled.MessageTrash>
                              )}
                            </Styled.MessageContentWrapper>
                          )}
                        </Styled.MessageBox>

                        <Styled.TimeSeenRow>
                          {showTime && <Styled.TimeBox>{message.time}</Styled.TimeBox>}
                          {showSeen && (
                            <>
                              {message.seenAt && <Tooltip id={message.id.toString()} />}
                              <Styled.StatusBox
                                active={message.seen}
                                data-tooltip-id={message.id.toString()}
                                data-tooltip-content={`${translate('glossary.seenAt')} ${
                                  message.seenAt
                                }`}
                              >
                                <TbChecks />
                              </Styled.StatusBox>
                            </>
                          )}
                        </Styled.TimeSeenRow>
                      </Styled.MessageWrapper>
                    );
                  })}
                </Styled.MessagesWrapper>
              </Styled.MessageSection>
            );
          })}
          {messageError && (
            <Styled.MessageWrapper ownMessage>
              <Styled.MessageBox ownMessage>
                <Styled.ErrorContentWrapper>
                  <Styled.MessageText ownMessage>
                    {translate('glossary.errorRefreshPage')}
                  </Styled.MessageText>
                </Styled.ErrorContentWrapper>
              </Styled.MessageBox>
            </Styled.MessageWrapper>
          )}
        </Styled.MessageSectionsWrapper>

        {otherAccountIsDeleted ? (
          <Styled.WarningWrapper>
            <InfoBox
              dangerouslySetInnerHTML={{
                __html: translate('mt.messages.accountDeleted'),
              }}
            />
          </Styled.WarningWrapper>
        ) : (
          <Form>
            {file && (
              <Styled.InputFileWrapper>
                {isImage(file.name) && <Styled.InputImage src={srcForFile(file)} alt="" />}

                {(isPdf(file.name) || isWord(file.name)) && file.name.split('/').pop()}

                <Styled.InputImageDeleteIcon onClick={() => setFieldValue('file', undefined)}>
                  <TbTrash />
                </Styled.InputImageDeleteIcon>
              </Styled.InputFileWrapper>
            )}
            <Styled.InputWrapper>
              <Styled.Input
                role="textbox"
                placeholder={translate('common.inputs.chatMessage.placeholder')}
                value={values.message}
                disabled={isSubmitting}
                onKeyDown={(event) => {
                  if (!mobileBreakpoint && !event.shiftKey && event.key === 'Enter') {
                    submitForm();
                  }
                }}
                onChange={(event) => {
                  setFieldValue('message', event.target.value);
                }}
              />
              <Styled.InputIconWrapper disabled={isSubmitting}>
                <InputFileField
                  disabled={isSubmitting}
                  name="file"
                  accept=".pdf, .doc, .docx, image/*, .heic, .heif"
                  icon={<TbPaperclip />}
                  iconOnly
                  isImage={false}
                />
              </Styled.InputIconWrapper>
              <Styled.InputIconWrapper as="button" disabled={!isValid || isSubmitting}>
                <TbSend />
              </Styled.InputIconWrapper>
            </Styled.InputWrapper>
          </Form>
        )}
      </Styled.ContentWrapper>
    </Styled.Wrapper>
  );
};
